import React, { useState } from 'react';
import AIAssistantWidget from './AIAssistantWidget';
import { openEmailDraft } from './utils/emailUtils';

export default function App() {
  const [flipped, setFlipped] = useState({});
  return (
    <div className="min-h-screen bg-gray-50 px-[5%]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
        <nav className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <a href="#" className="flex items-center gap-2">
            <img src="/borequo-ai-icon-black-blue.png" alt="Borequo AI" className="w-8 h-8" />
            <span className="text-xl font-bold text-gray-900">Borequo<span className="text-blue-900">AI</span></span>
          </a>
          <div className="hidden md:flex items-center gap-8">
            <a href="#" className="text-gray-700 hover:text-gray-900">Home</a>
            <a href="#solutions" className="text-gray-700 hover:text-gray-900">Solutions</a>
            <a href="#value" className="text-gray-700 hover:text-gray-900">Value</a>
            <a href="#security" className="text-gray-700 hover:text-gray-900">Security</a>
            <button onClick={() => openEmailDraft('book-briefing')} className="bg-blue-900 text-white px-5 py-2 rounded hover:bg-blue-800">
              Book an Executive Briefing
            </button>
          </div>
        </nav>
      </header>

      {/* Spacer for fixed header */}
      <div className="h-14"></div>

      {/* Hero Section */}
      <section className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-wide" style={{ letterSpacing: '0.04em', wordSpacing: '0.1em', lineHeight: '1.2em' }}>
              Modernize your Enterprise Systems with Governed AI-Workflows
            </h1>
            <p className="text-lg text-gray-700 mb-8">
              We layer AI across your existing stack — legacy platforms, SaaS, and internal tools; so teams can search, analyze, and execute decisions faster without a rip-and-replace
            </p>
            <div className="space-y-3 mb-8">
              <div className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">✓</span>
                <div>
                  <span className="font-semibold text-gray-900">Faster decisions:</span>
                  <span className="text-gray-700"> Reduce time spent hunting for context across tools</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">✓</span>
                <div>
                  <span className="font-semibold text-gray-900">Less coordination cost:</span>
                  <span className="text-gray-700"> Fewer status meetings, fewer pings, fewer escalations.</span>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-blue-600 mt-1">✓</span>
                <div>
                  <span className="font-semibold text-gray-900">Safer AI in production:</span>
                  <span className="text-gray-700"> Governed access, logs, and reliability.</span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-4">
              <button onClick={() => openEmailDraft('book-briefing')} className="bg-blue-900 text-white px-6 py-3 rounded hover:bg-blue-800">
                Book a 15-Min Executive Briefing
              </button>
              <a href="#solutions" className="border border-gray-300 text-gray-900 px-6 py-3 rounded hover:bg-gray-50 inline-block">
                View Solutions
              </a>
            </div>
          </div>

          {/* AI Assistant Widget */}
          <AIAssistantWidget />
        </div>
      </section>
            
      {/* Solutions Section */}
      <section id="solutions" className="bg-white py-16">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end gap-4 mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Solutions We Deploy</h2>
              <div className="w-20 h-1 bg-blue-900 mt-2"></div>
            </div>
            <span className="text-sm text-gray-600 pb-1">High ROI, Low Disruption</span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              {
                title: 'Enterprise Knowledge Search',
                desc: 'Find answers instantly across all your systems and documents.',
                items: ['What did we commit to Customer H?', 'Where was the launch blocked and why?', 'Summarize security incidents in the last 30 days.']
              },
              {
                title: 'Business Intelligence Copilot',
                desc: 'Ask questions about your data in plain English — no SQL needed.',
                items: ['Why did churn increase in Q4?', 'What moved customer acquisition cost up last month?', 'Show margin by segment and explain the key drivers.']
              },
              {
                title: 'Executive Briefing Agent',
                desc: 'Get daily summaries of critical risks and priorities.',
                items: ['Top 5 risks with owners and next actions', 'Customer escalations and recurring themes', 'What changed since yesterday that matters?']
              },
              {
                title: 'Communications Copilot',
                desc: 'Draft emails and updates using context from your systems.',
                items: ['Draft a response to the board about Q3 results', 'Write an executive update on the incident — facts only', 'Summarize a 30-email thread into key decisions.']
              },
              {
                title: 'Contract & Policy Assistant',
                desc: 'Get instant answers to compliance and contract questions.',
                items: ['What are our termination rights with Vendor Y?', 'Which policy covers data retention for customer X?', 'Which contracts renew in the next 90 days?']
              }
            ].map((solution, i) => (
              <div 
                key={i} 
                className="relative h-52 cursor-pointer perspective"
                onMouseEnter={() => setFlipped(prev => ({...prev, [i]: true}))}
                onMouseLeave={() => setFlipped(prev => ({...prev, [i]: false}))}
                onClick={() => setFlipped(prev => ({...prev, [i]: !prev[i]}))}
              >
                <div className={`relative w-full h-full transition-transform duration-500 transform-style-3d ${flipped[i] ? 'rotate-y-180' : ''}`}>
                  {/* Front */}
                  <div className="absolute w-full h-full backface-hidden border border-gray-200 rounded-lg p-6 bg-white flex flex-col">
                    <h3 className="font-bold text-gray-900 mb-2">{solution.title}</h3>
                    <p className="text-sm text-gray-600">{solution.desc}</p>
                  </div>
                  {/* Back */}
                  <div className="absolute w-full h-full backface-hidden border border-gray-200 rounded-lg p-6 bg-blue-900 text-white rotate-y-180 flex flex-col">
                    <ul className="space-y-2 text-sm">
                      {solution.items.map((item, j) => (
                        <li key={j}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-col items-center justify-center mt-8 gap-4">
            <p className="text-gray-600">
              We also deliver custom solutions tailored to your specific needs
            </p>
            <button onClick={() => openEmailDraft('tell-us-needs')} className="bg-blue-900 text-white px-8 py-3 rounded hover:bg-blue-800">
              Tell us your needs
            </button>
          </div>
        </div>
      </section>

      {/* What You Gain */}
      <section id="value" className="py-16">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">What You Gain</h2>
          <div className="w-20 h-1 bg-blue-900 mb-8"></div>
          <div className="bg-white border border-gray-200 rounded-lg p-8 mb-8">
            <div className="grid md:grid-cols-4 divide-x divide-gray-200">
              <div className="text-center px-6">
                <div className="text-5xl font-bold text-blue-900 mb-2">15+</div>
                <div className="text-base text-gray-700"><span className="font-semibold">hours saved</span><br />per employee weekly</div>
              </div>
              <div className="text-center px-6">
                <div className="text-5xl font-bold text-blue-900 mb-2">60%</div>
                <div className="text-base text-gray-700"><span className="font-semibold">less time</span><br />to resolve tasks</div>
              </div>
              <div className="text-center px-6">
                <div className="text-5xl font-bold text-blue-900 mb-2">2x</div>
                <div className="text-base text-gray-700"><span className="font-semibold">customer acquisition</span><br />with faster responses</div>
              </div>
              <div className="text-center px-6">
                <div className="text-5xl font-bold text-blue-900 mb-2">100%</div>
                <div className="text-base text-gray-700"><span className="font-semibold">traceable answers</span><br />with source citations</div>
              </div>
            </div>
          </div>
          <p className="text-center text-sm text-gray-600 mb-8">
            We measure baseline vs. post-launch improvement
          </p>
          <div className="text-center">
            <button onClick={() => openEmailDraft('book-value-briefing')} className="bg-blue-900 text-white px-8 py-3 rounded hover:bg-blue-800">
              Book a Briefing
            </button>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="bg-white py-16">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Enterprise Security by Design</h2>
          <div className="w-20 h-1 bg-blue-900 mb-8"></div>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="flex flex-col justify-between">
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">✓</span>
                  <span className="text-gray-700">Deep expertise in complex enterprise data systems</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">✓</span>
                  <span className="text-gray-700">Your data stays secure—encrypted and access-controlled</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-blue-600 mt-1">✓</span>
                  <span className="text-gray-700">Track usage and measure performance improvements</span>
                </div>
              </div>
              <div className="mt-6">
                <button onClick={() => openEmailDraft('request-security')} className="bg-blue-900 text-white px-8 py-3 rounded hover:bg-blue-800">
                  Request Security details
                </button>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-blue-900 text-white rounded-lg p-6">
                <h3 className="font-bold mb-4">Security Controls</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>SSO & role-based access</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>End-to-end encryption</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>Complete audit logs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>Automatic PII redaction</span>
                  </li>
                </ul>
              </div>
              <div className="bg-blue-900 text-white rounded-lg p-6">
                <h3 className="font-bold mb-4">Deployment Options</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>Private VPC — no public access</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>On-premises — data never leaves</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span>✓</span>
                    <span>Hybrid-connect securely</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="bg-blue-950 text-white py-8 absolute left-0 right-0">
        <div className="flex justify-center items-center px-8">
          <span className="text-sm mr-2">Contact us:</span>
          <button onClick={() => openEmailDraft('general-contact')} className="text-sm flex items-center gap-1">
            <span className="underline">Email</span>
            <span className="text-xs">↗</span>
          </button>
        </div>
      </footer>
    </div>
  );
}
